/**
 * authClient
 *
 * Thin client-side replacement for @supabase/supabase-js (auth only).
 * Talks to /api/auth/* endpoints on the backend and persists the session in
 * localStorage. Surface area is intentionally drop-in compatible with the
 * Supabase auth calls the app previously used.
 *
 * Session shape stored in localStorage:
 *   {
 *     access_token: string,
 *     refresh_token: string,
 *     expires_at: number,   // unix seconds
 *     token_type: string,
 *     user: { id, email, email_confirmed_at, created_at }
 *   }
 */

import API_BASE_URL from '../config/api';

const STORAGE_KEY = 'bc.auth.session.v1';
const REFRESH_LEEWAY_SECONDS = 60;

// In-flight refresh promise to coalesce concurrent callers.
let refreshInFlight = null;

// Local subscribers for onAuthStateChange.
const listeners = new Set();
let nextListenerId = 1;

// Cross-tab channel (best-effort; falls back to storage event below).
let broadcastChannel = null;
if (typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined') {
  try {
    broadcastChannel = new BroadcastChannel('bc-auth');
    broadcastChannel.onmessage = (event) => {
      const { type, session } = event.data || {};
      if (type === 'SIGNED_IN' || type === 'SIGNED_OUT' || type === 'TOKEN_REFRESHED') {
        notifyListeners(type, session || readSession());
      }
    };
  } catch (_) {
    broadcastChannel = null;
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    const session = readSession();
    notifyListeners(session ? 'SIGNED_IN' : 'SIGNED_OUT', session);
  });
}

function readSession() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.access_token) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function writeSession(session) {
  if (typeof window === 'undefined') return;
  if (!session) {
    window.localStorage.removeItem(STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

function broadcast(type, session) {
  if (broadcastChannel) {
    try { broadcastChannel.postMessage({ type, session }); } catch (_) { /* ignore */ }
  }
}

function notifyListeners(event, session) {
  for (const cb of listeners) {
    try { cb(event, session); } catch (err) { console.error('authClient listener error:', err); }
  }
}

function emit(type, session) {
  notifyListeners(type, session);
  broadcast(type, session);
}

function normalizeSession(raw) {
  if (!raw) return null;
  return {
    access_token: raw.access_token,
    refresh_token: raw.refresh_token,
    expires_at: raw.expires_at,
    token_type: raw.token_type || 'bearer',
    user: raw.user || null,
  };
}

async function callApi(path, options = {}) {
  const url = `${API_BASE_URL}${path}`;
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  const config = {
    method: options.method || 'GET',
    headers,
  };
  if (options.body !== undefined) {
    config.body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
  }
  const response = await fetch(url, config);
  let data = null;
  try { data = await response.json(); } catch (_) { /* no body */ }
  if (!response.ok) {
    const message = (data && (data.error || data.message)) || `HTTP ${response.status}`;
    const err = new Error(message);
    err.status = response.status;
    throw err;
  }
  return data;
}

/** Returns true when the access_token is expired or within the leeway window. */
function isAccessTokenStale(session) {
  if (!session || !session.access_token) return true;
  if (!session.expires_at) return false;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return session.expires_at - nowSeconds <= REFRESH_LEEWAY_SECONDS;
}

async function refreshSession() {
  if (refreshInFlight) return refreshInFlight;

  const current = readSession();
  if (!current?.refresh_token) {
    return null;
  }

  refreshInFlight = (async () => {
    try {
      const data = await callApi('/auth/refresh', {
        method: 'POST',
        body: { refresh_token: current.refresh_token },
      });
      const next = normalizeSession(data?.session);
      if (!next) {
        writeSession(null);
        emit('SIGNED_OUT', null);
        return null;
      }
      writeSession(next);
      emit('TOKEN_REFRESHED', next);
      return next;
    } catch (err) {
      console.warn('authClient.refreshSession failed:', err?.message || err);
      writeSession(null);
      emit('SIGNED_OUT', null);
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/** Get the current session, refreshing if the access_token is stale. */
async function getSession() {
  const current = readSession();
  if (!current) return null;
  if (isAccessTokenStale(current)) {
    const refreshed = await refreshSession();
    return refreshed;
  }
  return current;
}

/** Get just the access_token (refreshes if needed). */
async function getAccessToken() {
  const session = await getSession();
  return session?.access_token || null;
}

/** Synchronous read of whatever is in storage (no refresh). */
function getSessionSync() {
  return readSession();
}

async function signInWithPassword({ email, password }) {
  const data = await callApi('/auth/login', {
    method: 'POST',
    body: { email, password },
  });
  const session = normalizeSession(data?.session);
  if (!session) {
    throw new Error('Login response missing session');
  }
  writeSession(session);
  emit('SIGNED_IN', session);
  return { data: { session, user: session.user }, error: null };
}

async function signOut() {
  const current = readSession();
  try {
    if (current?.access_token) {
      await callApi('/auth/logout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${current.access_token}` },
      });
    }
  } catch (err) {
    // Best-effort: even if the server call fails, clear local state.
    console.warn('authClient.signOut server call failed:', err?.message || err);
  }
  writeSession(null);
  emit('SIGNED_OUT', null);
  return { error: null };
}

async function resetPasswordForEmail(email, options = {}) {
  await callApi('/auth/forgot-password', {
    method: 'POST',
    body: { email, redirectTo: options.redirectTo },
  });
  return { data: {}, error: null };
}

/**
 * Verify a one-time email confirmation token (e.g. the `token` from a
 * /auth/v1/verify link, or `token_hash`). On success the returned session is
 * persisted to localStorage and a SIGNED_IN event is emitted.
 *
 * @param {{ token_hash: string, type?: string }} params
 */
async function verifyOtp({ token_hash, type = 'signup' }) {
  if (!token_hash) {
    const err = new Error('token_hash is required');
    return { data: null, error: err };
  }
  try {
    const data = await callApi('/auth/verify-otp', {
      method: 'POST',
      body: { token_hash, type },
    });
    const session = normalizeSession(data?.session);
    if (!session) {
      throw new Error('verify-otp response missing session');
    }
    writeSession(session);
    emit('SIGNED_IN', session);
    return { data: { session, user: session.user, type: data?.type || type }, error: null };
  } catch (err) {
    return { data: null, error: err };
  }
}

async function updateUser({ password }) {
  const token = await getAccessToken();
  if (!token) {
    const err = new Error('Not signed in');
    return { data: null, error: err };
  }
  await callApi('/auth/update-password', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: { password },
  });
  return { data: {}, error: null };
}

/**
 * Subscribe to auth state changes. Returns a shape compatible with the
 * previous `supabase.auth.onAuthStateChange(...)` return value.
 */
function onAuthStateChange(callback) {
  const id = nextListenerId++;
  const wrapped = (event, session) => callback(event, session);
  wrapped._id = id;
  listeners.add(wrapped);
  return {
    data: {
      subscription: {
        unsubscribe() {
          listeners.delete(wrapped);
        },
      },
    },
  };
}

/**
 * If the page URL hash contains Supabase auth tokens (after an email
 * confirmation or password-recovery redirect), parse them, store the session,
 * and clear the hash. Idempotent — safe to call on every app boot.
 *
 * Returns the recovery/signup type when a session was consumed, otherwise null.
 */
function consumeHashSession() {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash || '';
  if (!hash || hash.length < 2) return null;
  if (!/access_token=|type=recovery|type=signup/i.test(hash)) return null;

  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');
  const expiresInRaw = params.get('expires_in');
  const expiresAtRaw = params.get('expires_at');
  const tokenType = params.get('token_type') || 'bearer';
  const type = params.get('type') || null;

  if (!accessToken || !refreshToken) {
    return type;
  }

  const nowSeconds = Math.floor(Date.now() / 1000);
  const expiresAt = expiresAtRaw
    ? parseInt(expiresAtRaw, 10)
    : (expiresInRaw ? nowSeconds + parseInt(expiresInRaw, 10) : null);

  // We don't have a full user object from the hash; fill in what we know.
  // The next /api/auth/session or /api/auth/refresh call will hydrate it.
  let userId = null;
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1] || ''));
    userId = payload?.sub || null;
  } catch (_) { /* ignore */ }

  const session = normalizeSession({
    access_token: accessToken,
    refresh_token: refreshToken,
    expires_at: expiresAt,
    token_type: tokenType,
    user: userId ? { id: userId } : null,
  });

  writeSession(session);

  // Strip the auth bits from the URL hash so a refresh doesn't re-consume them.
  try {
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search
    );
  } catch (_) { /* ignore */ }

  emit('SIGNED_IN', session);
  return type;
}

export const authClient = {
  getSession,
  getSessionSync,
  getAccessToken,
  signInWithPassword,
  signOut,
  resetPasswordForEmail,
  updateUser,
  verifyOtp,
  onAuthStateChange,
  consumeHashSession,
};

export default authClient;
